import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  BreadcrumbObjectType,
  GetWorkspaceByObjectQuery,
  useGetRootWorkspacesQuery,
  useGetWorkspaceByObjectQuery,
} from '../generated/apollo-operations'
import { useGlobalContext } from './useGlobalContext'

/**
 * A "project" in the web app is a root workspace: a top-level workspace hanging directly under
 * the organization, so `id` is the root workspace id. Every root has a name and an order (DB
 * check constraints), but `Workspace.name` and `Workspace.order` are nullable in the schema
 * because nested workspaces may lack them, so the hook narrows them here.
 */
export type SimpleProject = {
  id: string
  name: string
  order: number
}

type OrgProjectsContextType = {
  selectedProject?: SimpleProject
  setSelectedProject: (project: SimpleProject | undefined) => void
  /** The organization's root workspaces sorted by order */
  projects: SimpleProject[]
  /** True until the root workspaces have loaded */
  projectsLoading: boolean
  /** The selected root with its views and action buttons loaded */
  workspace?: GetWorkspaceByObjectQuery['workspaceByObject']
}

export function OrgProjectsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useGlobalContext()
  const [selectedProject, setSelectedProject] = useState<SimpleProject | undefined>()
  const { data: projectsData, loading: projectsLoading } = useGetRootWorkspacesQuery({
    skip: !user,
  })
  const { data: workspaceData } = useGetWorkspaceByObjectQuery({
    variables: {
      input: {
        objectId: selectedProject?.id || '',
        objectType: BreadcrumbObjectType.PROJECT,
      },
    },
    skip: !selectedProject || !user,
  })

  // Projects are the org's root workspaces, already sorted and role-filtered by the server
  const projects = useMemo(
    () =>
      (projectsData?.rootWorkspaces ?? []).map((root) => ({
        id: root.id,
        name: root.name ?? '',
        order: root.order ?? 0,
      })),
    [projectsData],
  )
  const workspace = useMemo(() => workspaceData?.workspaceByObject, [workspaceData])

  // Default to the first root once the list loads and if the selected root is deleted
  useEffect(() => {
    if (!projectsData) {
      return
    }
    if (!selectedProject || !projects.some((project) => project.id === selectedProject.id)) {
      setSelectedProject(projects.at(0))
    }
  }, [projectsData, projects, selectedProject])

  return (
    <OrgProjectsContext.Provider
      value={{ selectedProject, setSelectedProject, projects, projectsLoading, workspace }}
    >
      {children}
    </OrgProjectsContext.Provider>
  )
}

export const useOrgProjects = () => {
  const context = useContext(OrgProjectsContext)
  if (!context) {
    throw new Error('useOrgProjects must be used within a OrgProjectsProvider')
  }
  return context
}

const OrgProjectsContext = createContext<OrgProjectsContextType | undefined>(undefined)
